import { useId, useMemo, useState, type FormEvent } from "react";
import { CalendarClock, CalendarPlus, Circle, CircleCheckBig, Flame, Plus, Trash2 } from "lucide-react";
import { formatDue, taskBucket, toDateTimeLocal } from "./format";
import { PRIORITIES, PRIORITY_META, type AdminLead, type LeadPriority, type LeadTask } from "./types";
import type { TaskInput } from "./api";

// Pojedynczy wiersz zadania — używany w widoku Zadania, na pulpicie i w panelu leada.
export function TaskRow({
  task,
  lead,
  showLead = true,
  onToggle,
  onReschedule,
  onDelete,
  onOpenLead
}: {
  task: LeadTask;
  lead?: AdminLead | null;
  showLead?: boolean;
  onToggle: (task: LeadTask, done: boolean) => void;
  onReschedule?: (task: LeadTask) => void;
  onDelete?: (task: LeadTask) => void;
  onOpenLead?: (id: string) => void;
}) {
  const bucket = taskBucket(task.dueAt, task.done);
  return (
    <div className={`crm-task tone-${bucket}${task.done ? " is-done" : ""}`}>
      <button
        type="button"
        className="crm-task-check"
        onClick={() => onToggle(task, !task.done)}
        title={task.done ? "Cofnij" : "Oznacz jako zrobione"}
        aria-label={task.done ? "Oznacz jako niezrobione" : "Oznacz jako zrobione"}
      >
        {task.done ? <CircleCheckBig size={20} /> : <Circle size={20} />}
      </button>

      <div className="crm-task-main">
        <strong className="crm-task-title">{task.title}</strong>
        <div className="crm-task-meta">
          <span className={`crm-task-due tone-${bucket}`}>{formatDue(task.dueAt, task.done)}</span>
          {showLead && lead && (
            <button type="button" className="crm-task-lead" onClick={() => onOpenLead?.(lead.id)}>
              {lead.name}
            </button>
          )}
          {task.owner ? (
            <span className="crm-task-owner">{task.owner}</span>
          ) : (
            <span className="crm-task-owner none">Nieprzypisane</span>
          )}
          {task.priority === "high" && !task.done && (
            <span className="crm-task-prio">
              <Flame size={12} aria-hidden="true" /> Pilne
            </span>
          )}
        </div>
      </div>

      <div className="crm-task-actions">
        {!task.done && onReschedule && (
          <button
            type="button"
            className="crm-icon-btn sm"
            title="Przełóż o 1 dzień"
            onClick={() => onReschedule(task)}
          >
            <CalendarPlus size={15} aria-hidden="true" />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            className="crm-icon-btn sm danger"
            title="Usuń zadanie"
            onClick={() => onDelete(task)}
          >
            <Trash2 size={15} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

function presetDate(daysAhead: number, hour: number) {
  const date = new Date();
  date.setDate(date.getDate() + daysAhead);
  date.setHours(hour, 0, 0, 0);
  return toDateTimeLocal(date);
}

// Formularz nowego zadania — pełny (widok Zadania) lub z zablokowanym leadem (panel leada).
export function TaskForm({
  leads,
  defaultLeadId,
  lockLead = false,
  onSubmit
}: {
  leads: AdminLead[];
  defaultLeadId?: string;
  lockLead?: boolean;
  onSubmit: (input: TaskInput) => Promise<void>;
}) {
  const listId = useId();
  const [title, setTitle] = useState("");
  const [due, setDue] = useState(() => presetDate(0, 17));
  const [owner, setOwner] = useState("");
  const [priority, setPriority] = useState<LeadPriority>("normal");
  const [leadId, setLeadId] = useState(defaultLeadId ?? "");
  const [busy, setBusy] = useState(false);

  const owners = useMemo(() => {
    const set = new Set<string>();
    leads.forEach((lead) => {
      if (lead.owner) set.add(lead.owner);
    });
    return [...set].sort((a, b) => a.localeCompare(b, "pl"));
  }, [leads]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!title.trim() || !due || busy) return;
    setBusy(true);
    try {
      await onSubmit({
        title: title.trim(),
        dueAt: new Date(due).toISOString(),
        leadId: leadId || undefined,
        owner: owner.trim() || undefined,
        priority
      });
      setTitle("");
      setPriority("normal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="crm-task-form" onSubmit={submit}>
      <div className="crm-task-form-top">
        <CalendarClock size={18} className="crm-task-form-icon" aria-hidden="true" />
        <input
          className="crm-task-form-title"
          value={title}
          placeholder="Co trzeba zrobić? (np. Oddzwonić do klienta)"
          onChange={(event) => setTitle(event.target.value)}
        />
        <button className="crm-btn crm-btn--primary" type="submit" disabled={busy || !title.trim()}>
          <Plus size={16} aria-hidden="true" /> Dodaj
        </button>
      </div>

      <div className="crm-task-form-row">
        <label className="crm-field">
          <span>Termin</span>
          <input type="datetime-local" value={due} onChange={(event) => setDue(event.target.value)} />
        </label>
        <label className="crm-field">
          <span>Opiekun</span>
          <input
            list={listId}
            value={owner}
            placeholder="Nieprzypisane"
            onChange={(event) => setOwner(event.target.value)}
          />
          <datalist id={listId}>
            {owners.map((o) => (
              <option key={o} value={o} />
            ))}
          </datalist>
        </label>
        <label className="crm-field">
          <span>Priorytet</span>
          <select
            className="crm-select"
            value={priority}
            onChange={(event) => setPriority(event.target.value as LeadPriority)}
          >
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_META[p].label}
              </option>
            ))}
          </select>
        </label>
        {!lockLead && (
          <label className="crm-field">
            <span>Powiązany lead</span>
            <select className="crm-select" value={leadId} onChange={(event) => setLeadId(event.target.value)}>
              <option value="">— bez leada —</option>
              {leads.map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {lead.name}
                  {lead.city ? ` · ${lead.city}` : ""}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="crm-task-presets">
        <span>Szybki termin:</span>
        <button type="button" onClick={() => setDue(presetDate(0, 17))}>
          Dziś 17:00
        </button>
        <button type="button" onClick={() => setDue(presetDate(1, 9))}>
          Jutro 9:00
        </button>
        <button type="button" onClick={() => setDue(presetDate(3, 9))}>
          Za 3 dni
        </button>
        <button type="button" onClick={() => setDue(presetDate(7, 9))}>
          Za tydzień
        </button>
      </div>
    </form>
  );
}
